
import { mapState, mapGetters, mapActions } from "vuex";
import { spacesToDashes } from "~/common/helpers";
import { generateProfileDynamicMeta } from "~/common/seo/profile-dynamic-meta";
import { mdiArrowLeft, mdiArrowRight, mdiHeart } from "@mdi/js";
import ScrollToTopButton from "~/components/v2/ui/ScrollToTopButton.vue";

export default {
  name: "ProfilePage",

  components: {
    AboutMeModal: () =>
      import("~/components/v2/modals/profiles/about-me-modal.vue"),
    BookMeModal: () =>
      import("~/components/v2/modals/profiles/book-me-modal.vue"),
    ScrollToTopButton,
  },

  provide() {
    return {
      _imgAlt: this._imgAlt,
      _h1: this._h1,
    };
  },

  layout: "slug",

  data() {
    return {
      route: this.$route.params.slug,

      mdiArrowLeft,
      mdiArrowRight,
      mdiHeart,

      activeNames: ["1"],

      activeTour: {},

      buttonShow: true,

      currentComponent: "profile-gallery",

      currentGalleryInMobile: "profile-gallery",

      isFavoriteProcessing: false,

      isScrolled: false,
      isScrolledBottom: false,

      imgTopSpacing: 0,

      isTopMobileActionsHidden: false,

      windowInnerHeight: 300,

      modalSendMessage: false,
    };
  },

  async fetch({ store, params, route, redirect }) {
    try {
      const idModel = route.params.slug.replace(/\D/gi, "");

      await store.dispatch("profiles/retrieveCurrentProf", params);
      await store.dispatch("comments/getProfileReviews", idModel);
      await store.dispatch("profiles/retrieveFilters");
      store.dispatch("favourites/retrieveFavouritesModels");
    } catch (err) {
      if (err.message == "Profile not fount") {
        redirect(301, "/");
        return;
      }
      if (err.message == "Redirect") {
        const path = err.to.startsWith("/") ? err.to : "/profile/" + err.to;
        redirect(Number(err.code) || 301, path);
        return;
      }
    }
  },

  head() {
    const heroMobile =
      this.profile.background_mobile || this.profile.background;
    const heroDesktop =
      this.profile.background_desktop || this.profile.background;
    const shouldPreload = !this.discreetMode && this.profile.background;

    return {
      title: this.metaTitle,

      link: [
        {
          rel: "canonical",
          href: this.$config.baseUrlApp + this.$route.path,
        },

        ...(shouldPreload
          ? [
              {
                rel: "preload",
                as: "image",
                href: heroMobile,
                media: "(width <= 600px)",
                fetchpriority: "high",
                hid: "hero-bg-preload-mobile",
              },
              {
                rel: "preload",
                as: "image",
                href: heroDesktop,
                media: "(width > 600px)",
                fetchpriority: "high",
                hid: "hero-bg-preload-desktop",
              },
            ]
          : []),
      ],

      meta: [
        {
          hid: "description",
          name: "description",
          content: this.metaDescription,
        },
        {
          hid: "og:description",
          name: "og:description",
          content: this.metaDescription,
        },
        {
          hid: "og:title",
          name: "og:title",
          content: this.metaTitle,
        },
        {
          property: "og:image",
          hid: "og:image",
          name: "og:image",
          content: this.profile.mainPhoto,
        },
        {
          name: "robots",
          content: this.districtId ? "noindex" : "all",
        },
      ],

      script: [
        {
          type: "application/ld+json",
          innerHTML: JSON.stringify(this.jsonldProfile),
        },
      ],

      __dangerouslyDisableSanitizers: ["script"],
    };
  },

  computed: {
    ...mapState(["viewedModels"]),

    ...mapGetters({
      profile: "profiles/getProfile",
      discreetMode: "getDiscreetStatus",
      locationUrl: "global/getCurrentLocationUrl",
      getNationalityById: "profiles/getNationalityById",
      filtersList: "profiles/getFiltersList",
      processedSeoTexts: "global/getProcessedSeoTexts",
    }),

    districtId() {
      return this.$route.query.did;
    },

    decodedDistrict() {
      let district;

      if (this.districtId) {
        district = this.filtersList?.districts?.find(
          (f) => f.id == this.districtId
        );
      }

      return district;
    },

    districtUrl() {
      if (!this.decodedDistrict) return "";
      return (
        `/${this.profileCityUrl}-` + spacesToDashes(this.decodedDistrict.title)
      );
    },

    isMobile() {
      return this.$store.state.global.mobile;
    },

    profileCity() {
      if (!this.profile.location) return "";
      return this.profile.location[0].baseCity;
    },

    profileCityId() {
      if (!this.profile.location) return "";
      return this.profile.location[0].baseCityId;
    },

    profileCityUrl() {
      if (!this.profile.location) return "";
      return spacesToDashes(this.profileCity);
    },

    profilePhone() {
      let md = this.profile?.myDetails;
      if (!md || !md[0]) return;
      return md[0]?.phone;
    },

    isFavorite() {
      return this.profile.isFavorite;
    },

    profileDynamicMeta() {
      return generateProfileDynamicMeta(this.profile);
    },

    metaTitle() {
      return this.processedSeoTexts?.title || this.profileDynamicMeta.title;
    },

    metaDescription() {
      return (
        this.processedSeoTexts?.description ||
        this.profileDynamicMeta.description
      );
    },

    crumbs() {
      let pos = 1;

      const crumbs = [];

      crumbs.push({ href: "/", text: "Home", pos: pos++, disabled: false });
      crumbs.push({
        href: `/${this.profileCityUrl}`,
        text: this.profileCity,
        pos: pos++,
        disabled: false,
      });
      let locationUrl = `/${this.profileCityUrl}`;
      crumbs.push({
        href: `${locationUrl}/${this.profile?.gender?.toLowerCase()}`,
        text: this.profile.gender,
        pos: pos++,
        disabled: false,
      });
      crumbs.push({
        href: `/profile/${this.profile.slug}`,
        text: this.profile.name,
        pos: pos++,
        disabled: false,
      });

      return crumbs;
    },

    imgSpacingCSS() {
      if (!process.client) return;
      return {
        "--viewport-height": this.windowInnerHeight + "px",
        "--img-spacing": this.imgTopSpacing + "px",
      };
    },

    showTabOptions() {
      return {
        gallery: this.profile.gallerySecond.length,
        selfie: this.profile.galleryFirst.length,
        video: this.profile.video.length,
      };
    },

    initGalleryTab() {
      let tab = "gallery";
      if (this.profile.gallerySecond.length) {
        tab = "gallery";
      } else if (this.profile.galleryFirst.length) {
        tab = "selfie";
      } else if (this.profile.video.length) {
        tab = "video";
      }
      return tab;
    },

    hideSendPmBtn() {
      return this.$auth.loggedIn && this.$auth.user.user_role != 3;
    },

    jsonldProfile() {
      if (!this.profile || !this.profile?.name || !this.profile?.slug) return;

      return {
        "@context": "http://schema.org",
        "@type": "ProfilePage",
        mainEntity: {
          "@type": "Person",
          name: this.profile.name,
          url: this.$config.baseUrlApp + this.profile.slug,
          description: this.profile.description,
          image: this.profile.mainPhoto,
          address: {
            "@type": "PostalAddress",
            addressLocality: this.profileCity,
            addressRegion: this.decodedDistrict?.title,
            addressCountry: "United Arab Emirates",
          },
          memberOf: {
            "@type": "Organization",
            name: "Emiratescort",
          },
        },
      };
    },

    nationality() {
      return this.getNationalityById(this.profile.myDetails[0].nationality);
    },

    backToCatalogUrl() {
      if (
        document.referrer &&
        document.referrer.includes(window.location.hostname) &&
        document.referrer !== window.location.href.split("?")[0]
      ) {
        return document.referrer;
      } else {
        return `/${this.profileCityUrl}/${this.profile.gender.toLowerCase()}`;
      }
    },

    _imgAlt() {
      return `${this.profile.name} - ${this.nationality} ${this.profile.location[0].baseCity} - ${this.profile.id}`;
    },

    _h1() {
      return `${this.profile.name}, ${this.profile.myDetails[0].age}, ${
        this.profile.myDetails[0].hairColor
      }, ${this.nationality}, ${this._seo_h1_gender}${this.$t(
        "escort-in-city",
        [this.profile.location[0].baseCity]
      )} - ${this.profile.id}`;
    },

    _seo_h1_gender() {
      const gender = this.profile?.gender?.toLowerCase();
      switch (gender) {
        case "male":
          return " male ";
        case "transgender":
          return " transsexual ";
        default:
          return " ";
      }
    },

    scrollToBtnPossition() {
      if (this.isMobile) {
        return {
          bottom: this.isScrolledBottom ? 170 : 100,
          right: 28,
        };
      }
      return {
        bottom: 30,
        right: 30,
      };
    },

    heroBackgroundStyle() {
      if (!this.profile.background || this.discreetMode) return {};
      return {
        "--hero-bg-mobile": `url(${
          this.profile.background_mobile || this.profile.background
        })`,
        "--hero-bg-desktop": `url(${
          this.profile.background_desktop || this.profile.background
        })`,
      };
    },
  },

  beforeMount() {
    window.addEventListener("scroll", this.effectScroll);
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.effectScroll);
    this.$root.$off("book-me-modal:open");
  },

  mounted() {
    this.scrollToTop();
    this.checkActiveTour();
    if (this.$refs.nameWithTags) {
      const headerHeight = 60;
      const bottomSpacing = 20;
      this.imgTopSpacing =
        this.$refs.nameWithTags.clientHeight + headerHeight + bottomSpacing;
    }

    this.$nextTick(() => {
      if (this.$refs.mobileActions) {
        this.imgTopSpacing += this.$refs.mobileActions.clientHeight;
      }
    });

    document.addEventListener(
      "scroll",
      () => {
        this.isScrolled = window.scrollY > 100;
        this.isScrolledBottom =
          window.scrollY + window.innerHeight >=
          document.body.scrollHeight - 100;
      },
      {
        passive: true,
      }
    );

    // observe top-mobile-actions
    // this.$nextTick(() => {
    //   const anchors = document.querySelector("#top-mobile-actions");
    //   const observer = new IntersectionObserver(
    //     ([entry]) => {
    //       this.isTopMobileActionsHidden = !entry.isIntersecting;
    //     },
    //     {
    //       root: null,
    //       rootMargin: "0px",
    //       threshold: 0,
    //     }
    //   );

    //   if (anchors) {
    //     observer.observe(anchors);
    //   }
    // });

    this.updateInnerHeight();

    this.$root.$on("book-me-modal:open", () => {
      this.$refs.bookMeModal.open();
    });
  },

  // unmounted() {
  //   window.removeEventListener("resize", this.updateInnerHeight);
  // },

  methods: {
    ...mapActions({
      addToFafourites: "profiles/addToFafourites",
      removeFromFavourites: "profiles/removeFromFavourites",
    }),

    scrollToReviews() {
      const reviews = document.querySelector("#comments-review");

      if (reviews) {
        reviews.scrollIntoView({ behavior: "smooth" });
      }
      return;
    },

    openServicesModal() {
      this.$root.$emit("open-services-modal");
    },

    openRatesModal() {
      this.$root.$emit("open-rates-modal");
    },

    formattedDateForTour(date) {
      return date
        .split("-")
        .map((d, i) => (i > 0 ? d : ""))
        .reverse()
        .splice(0, 2)
        .join(".");
    },

    updateInnerHeight() {
      this.windowInnerHeight = window.innerHeight;
    },

    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },

    effectScroll() {
      const minOpacity = this.isMobile ? 0.15 : 0;
      const scrollDistance = window.scrollY;
      const blur = scrollDistance / 100;
      const opacity = 1 - scrollDistance / 500;
      this.$refs.background.style.filter = `blur(${blur}px)`;
      this.$refs.background.style.opacity =
        opacity < minOpacity ? minOpacity : opacity;

      // if (this.isMobile) {
      //   if (!this.$refs.control || !this.$refs.controlTrigger) return;
      //   const controlTriggerRect =
      //     this.$refs.controlTrigger.getBoundingClientRect();
      //   this.$refs.control.classList.toggle(
      //     "unFixed",
      //     controlTriggerRect.top < window.innerHeight
      //   );
      // }
    },

    changeTab(value) {
      this.currentComponent = value;
    },

    mobileChangeTab(value) {
      this.currentGalleryInMobile = value;
    },

    checkActiveTour() {
      if (!this.profile.serviceDetails[0]?.tours) return;
      const now = new Date();
      for (let tour of this.profile.serviceDetails[0].tours) {
        const dateFrom = new Date(tour.date_from);
        const dateTo = new Date(tour.date_to);
        if (now >= dateFrom && now <= dateTo) {
          this.activeTour = tour;
        }
      }
    },

    async processFavorite() {
      if (!this.$auth.loggedIn) {
        this.this.redirectOnAuth();
        return;
      }
      this.isFavoriteProcessing = true;
      let response;
      if (this.isFavorite) {
        response = await this.removeFromFavourites(this.profile.id);
      } else {
        response = await this.addToFafourites(this.profile.id);
      }

      if (!response.data.status) {
        this.$notify({
          type: "warning",
          message: response.data.msg,
        });
      }

      this.isFavoriteProcessing = false;
    },

    openMessageDialog() {
      if (this.$auth.loggedIn) {
        this.$root.$emit("open-message-dialog");
      } else {
        this.redirectOnAuth();
      }
    },

    redirectOnAuth() {
      if (window.innerWidth < 1024) {
        this.$root.$emit("sign-in-drawer:open");
      } else {
        this.$router.push({ name: "auth-signin" });
      }
    },

    openAboutMeModal() {
      this.$refs.aboutMeModal.open();
    },

    openAskMeModal() {
      this.$root.$emit("open-ask-me-modal");
    },
  },
};
